/**
 * Tile icons — hand-drawn-feel single-file SVG glyphs.
 *
 * Round-8 FRONTEND-POLISH delivers one icon per module (15 total) plus a
 * generic fallback + a chevron + a search glyph used by the rail / header
 * search affordance. Built as small JSX `ReactNode` factories so the
 * caller can tint them via `currentColor` (we render the tile slug in
 * `var(--accent)` so the icon picks that up for free).
 *
 * Why inline + hand-rolled instead of Lucide? Lucide is banned per the
 * BQB signature — its line-icon vocabulary reads as the canonical AI
 * template tell. These are intentionally NOT outline-only: each glyph
 * has at least one filled blob to break the Lucide silhouette.
 *
 * Sizing: every icon is a 24×24 viewBox + a `width/height={size}` prop
 * (default 18). Stroke is `currentColor` at 1.6 px; fills use
 * `currentColor` with 0.18 / 0.32 opacity. The container picks the colour.
 *
 * Bundle budget per the R8 brief: < 5 KB raw across the 16 glyphs. This
 * file is currently ~3.8 KB minified; well inside the budget.
 */
import type { CSSProperties, ReactElement, SVGProps } from 'react';
import type { ModuleSlug } from '@/styles/tokens';
import { DEFAULT_MODULE_TREE } from '@/shared/modules/catalog';
import { useModuleRegistries } from '@/shared/modules/store';
import { deriveModuleIcons } from '@/shared/modules/registries';

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> {
  size?: number;
}

function svg(
  size: number,
  body: ReactElement | ReactElement[],
  rest: Omit<SVGProps<SVGSVGElement>, 'width' | 'height' | 'children'>,
): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...rest}
    >
      {body}
    </svg>
  );
}

const fill18: CSSProperties = { fill: 'currentColor', fillOpacity: 0.18, stroke: 'none' };
const fill32: CSSProperties = { fill: 'currentColor', fillOpacity: 0.32, stroke: 'none' };

// ── CS Portal — antenna sweeping over a horizon line ───────────────
export function IconCoportal({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <circle cx={12} cy={13} r={1.6} style={fill32} />
      <path d="M5 18.5h14" />
      <path d="M12 13l-4-6" />
      <path d="M12 13l4-6" />
      <path d="M7 5.5l10 0" />
    </>,
    rest,
  );
}

// ── Res Tools — folded itinerary square ────────────────────────────
export function IconRestools({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <path
        d="M5.5 5.5h13v13h-13z"
        style={{ fill: 'currentColor', fillOpacity: 0.18, stroke: 'currentColor' }}
      />
      <path d="M9 5.5v13" />
      <path d="M5.5 12h13" />
      <circle cx={14.5} cy={9} r={1} style={fill32} />
    </>,
    rest,
  );
}

// ── Lemax — bug-with-antennae (issue tracker) ──────────────────────
export function IconLemax({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <path d="M8 8l-2.5-2" />
      <path d="M16 8l2.5-2" />
      <rect x={7} y={8} width={10} height={10} rx={4} style={fill18} />
      <path d="M5 14h2.5" />
      <path d="M16.5 14H19" />
      <circle cx={10.5} cy={12} r={0.8} style={fill32} />
      <circle cx={13.5} cy={12} r={0.8} style={fill32} />
    </>,
    rest,
  );
}

// ── Contracting — pen-on-paper signature ───────────────────────────
export function IconContracting({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <path
        d="M6 5h9l3 3v11H6z"
        style={{ fill: 'currentColor', fillOpacity: 0.18, stroke: 'currentColor' }}
      />
      <path d="M9 14c1.5-1 2.5-1 3.5 0s2 .5 3-.5" />
      <path d="M14.5 5v3.5H18" />
    </>,
    rest,
  );
}

// ── HR — two-person silhouette ─────────────────────────────────────
export function IconHr({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <circle cx={9} cy={9} r={2.6} style={fill32} />
      <circle cx={15.5} cy={9.5} r={2.1} />
      <path d="M4.5 18c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" />
      <path d="M14 18c.2-2 1.5-3.2 3.2-3.2s2.8 1 3 3" />
    </>,
    rest,
  );
}

// ── IT — terminal cursor with caret ────────────────────────────────
export function IconIt({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <rect x={4} y={6} width={16} height={12} rx={2} style={fill18} />
      <path d="M7.5 10.5l2.2 2-2.2 2" />
      <path d="M11 14.5h4" />
    </>,
    rest,
  );
}

// ── Finance — ledger column with bar marks ─────────────────────────
export function IconFinance({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <path d="M6 18V8" />
      <path d="M10 18V11" />
      <path d="M14 18V6" />
      <path d="M18 18V13" />
      <path d="M5 18.5h14" />
      <circle cx={14} cy={6} r={1.2} style={fill32} />
    </>,
    rest,
  );
}

// ── Data — stacked discs ───────────────────────────────────────────
export function IconData({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <ellipse cx={12} cy={7} rx={6} ry={2.2} style={fill18} />
      <path d="M6 7v4c0 1.2 2.7 2.2 6 2.2s6-1 6-2.2V7" />
      <path d="M6 11v4c0 1.2 2.7 2.2 6 2.2s6-1 6-2.2v-4" />
    </>,
    rest,
  );
}

// ── Ecommerce — handbag / shopping pouch ───────────────────────────
export function IconEcommerce({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <path
        d="M6 9h12l-1 10H7z"
        style={{ fill: 'currentColor', fillOpacity: 0.18, stroke: 'currentColor' }}
      />
      <path d="M9 9V7a3 3 0 0 1 6 0v2" />
    </>,
    rest,
  );
}

// ── Bizdev — handshake (two crossed crescents) ─────────────────────
export function IconBizdev({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <path d="M4 13l4-3 4 3" />
      <path d="M20 13l-4-3-4 3" />
      <path
        d="M9 14l3 2 3-2"
        style={{ fill: 'currentColor', fillOpacity: 0.18, stroke: 'currentColor' }}
      />
      <circle cx={12} cy={16} r={0.9} style={fill32} />
    </>,
    rest,
  );
}

// ── Product — production line / paper roll ─────────────────────────
export function IconProduct({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <rect x={5} y={6} width={11} height={12} rx={1.6} style={fill18} />
      <path d="M16 8h2.5l.5 2v6c0 1-.7 1.5-1.5 1.5S16 17 16 16V8z" />
      <path d="M8 10h5" />
      <path d="M8 13h5" />
    </>,
    rest,
  );
}

// ── Management — pyramid / org chart ───────────────────────────────
export function IconManagement({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <circle cx={12} cy={6.5} r={1.6} style={fill32} />
      <circle cx={7.5} cy={14} r={1.4} />
      <circle cx={16.5} cy={14} r={1.4} />
      <path d="M12 8v4" />
      <path d="M12 12L8 13" />
      <path d="M12 12l4 1" />
    </>,
    rest,
  );
}

// ── AI AI AI — sparkle cluster ─────────────────────────────────────
export function IconAiaiai({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <path
        d="M12 5l1.5 3.5L17 10l-3.5 1.5L12 15l-1.5-3.5L7 10l3.5-1.5z"
        style={{ fill: 'currentColor', fillOpacity: 0.32, stroke: 'currentColor' }}
      />
      <path d="M17.5 15.5l.8 1.8 1.8.7-1.8.7-.8 1.8-.7-1.8-1.8-.7 1.8-.7z" />
    </>,
    rest,
  );
}

// ── Projects — three staggered bars on a timeline, a flag on the last ──
// For a tenant that runs the `aiaiai` module as "Projects" (brief §8.3):
// its module config can name the icon key `milestones`. Asian Trails keeps
// the sparkles.
export function IconProjects({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <rect x={4} y={5} width={8} height={3} rx={1.2} style={fill32} />
      <rect x={7} y={10.5} width={9} height={3} rx={1.2} />
      <rect x={10} y={16} width={7} height={3} rx={1.2} style={fill18} />
      <path d="M19.5 15V5" />
      <path d="M19.5 5.5l-3 1.6 3 1.6" style={{ fill: 'currentColor', fillOpacity: 0.32 }} />
    </>,
    rest,
  );
}

// ── Admin — gear with notched ring ─────────────────────────────────
export function IconAdmin({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <circle cx={12} cy={12} r={3} style={fill32} />
      <path d="M12 4v2.5" />
      <path d="M12 17.5V20" />
      <path d="M4 12h2.5" />
      <path d="M17.5 12H20" />
      <path d="M6.4 6.4l1.7 1.7" />
      <path d="M15.9 15.9l1.7 1.7" />
      <path d="M17.6 6.4l-1.7 1.7" />
      <path d="M8.1 15.9l-1.7 1.7" />
    </>,
    rest,
  );
}

// ── Preferences — slider knob ──────────────────────────────────────
export function IconPreferences({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <path d="M4 8h11" />
      <circle cx={17} cy={8} r={2} style={fill32} />
      <path d="M19 16h-11" />
      <circle cx={7} cy={16} r={2} />
    </>,
    rest,
  );
}

// ── Generic — soft 4-petal asterisk ────────────────────────────────
export function IconGeneric({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
      <path d="M7 7l10 10" />
      <path d="M17 7l-10 10" />
      <circle cx={12} cy={12} r={1.6} style={fill32} />
    </>,
    rest,
  );
}

// ── Search — magnifier (used by header search affordance) ──────────
export function IconSearch({ size = 16, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <circle cx={10.5} cy={10.5} r={5} />
      <path d="M19 19l-4.5-4.5" />
    </>,
    rest,
  );
}

// ── Chevron — used for caret-right indicators ──────────────────────
export function IconChevronRight({ size = 14, ...rest }: IconProps) {
  return svg(size, <path d="M9 6l6 6-6 6" />, rest);
}

// ── Phone — the hotline rota card on Weekly Arrivals ───────────────
// Added rather than importing lucide-react for a single glyph: PHP loads
// lucide from a CDN, React ships this hand-rolled set instead.
export function IconPhone({ size = 14, ...rest }: IconProps) {
  return svg(
    size,
    <path d="M6.5 3h3l1.5 3.7-1.9 1.2a10.5 10.5 0 0 0 4.9 4.9l1.2-1.9L19 12.5v3a1.8 1.8 0 0 1-2 1.8A14.4 14.4 0 0 1 4.7 5 1.8 1.8 0 0 1 6.5 3z" />,
    rest,
  );
}

// ── Employee Hub — a friendly two-person mark ───────────────────────
// (Moved from the Hub page, 2026-10-15: the catalog names it `pair`.)
export function IconPair({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <circle cx={9} cy={8} r={3} style={fill18} />
      <circle cx={9} cy={8} r={3} />
      <path d="M3.5 19c0-3 2.4-5 5.5-5s5.5 2 5.5 5" />
      <circle cx={17.5} cy={9.5} r={2.1} />
      <path d="M15.3 19c.1-2.2 1-3.9 3-4.4" />
    </>,
    rest,
  );
}

// ── Flag: an incident, something raised (custom modules, 2026-10-15) ──
export function IconFlag({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <path d="M6 21V4" />
      <path d="M6 5c3-1.6 5.2 1.4 8 0s3.6-.6 4 .2v7.6c-.4-.8-1.6-1.4-4-.2s-5-1.6-8 0z" style={fill18} />
      <path d="M6 5c3-1.6 5.2 1.4 8 0s3.6-.6 4 .2v7.6c-.4-.8-1.6-1.4-4-.2s-5-1.6-8 0" />
    </>,
    rest,
  );
}

// ── Tag: a labelled item, kept until claimed (custom modules, 2026-10-15) ──
export function IconTag({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <path d="M4 12.6V5.2C4 4.5 4.5 4 5.2 4h7.4l7 7-8.4 8.4z" style={fill18} />
      <path d="M4 12.6V5.2C4 4.5 4.5 4 5.2 4h7.4l7 7-8.4 8.4z" />
      <circle cx={8.6} cy={8.6} r={1.4} style={fill32} />
    </>,
    rest,
  );
}

// ── Guest Portal — a door left open for the guest ──────────────────
export function IconDoor({ size = 18, ...rest }: IconProps) {
  return svg(
    size,
    <>
      <path d="M6.5 20V5.2a1.2 1.2 0 0 1 1.2-1.2h8.6a1.2 1.2 0 0 1 1.2 1.2V20" />
      <path d="M4 20h16" />
      <path d="M6.5 4.4l6 1.9v13.1l-6 .6z" style={fill18} />
      <path d="M6.5 4.4l6 1.9v13.1l-6 .6" />
      <circle cx={10.6} cy={12.6} r={0.9} style={fill32} />
    </>,
    rest,
  );
}

// ── The curated set, by key ────────────────────────────────────────
//
// A module's `icon` in `shared/modules/catalog.json` is one of these keys
// (named for what the glyph shows, so a tenant picking an icon in Admin ▸
// Modules reads a word, not a module name). `catalog.test.ts` fails on a
// catalog icon that is not here.
export const MODULE_ICONS = {
  antenna: IconCoportal,
  itinerary: IconRestools,
  bug: IconLemax,
  signature: IconContracting,
  people: IconHr,
  terminal: IconIt,
  ledger: IconFinance,
  discs: IconData,
  bag: IconEcommerce,
  handshake: IconBizdev,
  roll: IconProduct,
  pyramid: IconManagement,
  sparkles: IconAiaiai,
  milestones: IconProjects,
  gear: IconAdmin,
  slider: IconPreferences,
  pair: IconPair,
  door: IconDoor,
  asterisk: IconGeneric,
  flag: IconFlag,
  tag: IconTag,
} as const satisfies Record<string, (props: IconProps) => ReactElement>;

export type ModuleIconKey = keyof typeof MODULE_ICONS;

/** The glyph for an icon key; the generic asterisk for an unknown one. */
export function iconForKey(key: string): (props: IconProps) => ReactElement {
  return (MODULE_ICONS as Record<string, (props: IconProps) => ReactElement>)[key] ?? IconGeneric;
}

// ── Module → component map (from each module's catalog `icon`) ────

export const ICON_FOR_MODULE: Record<ModuleSlug, (props: IconProps) => ReactElement> = Object.fromEntries(
  Object.entries(deriveModuleIcons(DEFAULT_MODULE_TREE)).map(([mod, key]) => [mod, iconForKey(key)]),
) as Record<ModuleSlug, (props: IconProps) => ReactElement>;

export function ModuleIcon({
  mod,
  size = 18,
  ...rest
}: IconProps & { mod: ModuleSlug }) {
  // The tenant's icon when Admin ▸ Modules set one, else the catalog's.
  const key = useModuleRegistries().icons[mod];
  const Cmp = (key ? iconForKey(key) : ICON_FOR_MODULE[mod]) ?? IconGeneric;
  return <Cmp size={size} {...rest} />;
}
